import React, { useState } from "react";
import { Popover, Spin } from "antd";
import { getOfficeNotes } from "./controller";

const MAX_NOTES = 10;

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch (e) {
    return "";
  }
}

/**
 * OfficeNotesPopover — hover an office's Practices-page cell to preview its most
 * recent CRM notes (up to 10, latest first). Notes are LAZY-fetched on the first
 * hover (getOfficeNotes) and cached, so the Practices list never fires N note
 * requests on load. Wraps the office row's primary cell; the row's own onClick
 * (open OfficeDetailDrawer) still works — this is a hover-only overlay.
 */
export default function OfficeNotesPopover({ officeId, officeName, children }) {
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [notes, setNotes] = useState([]);

  const handleOpenChange = (open) => {
    if (!open || loaded || loading || !officeId) return;
    setLoading(true);
    getOfficeNotes(officeId)
      .then((r) => {
        const list =
          r && r.status >= 200 && r.status < 300 && Array.isArray(r.json) ? r.json : [];
        setNotes(list.slice(0, MAX_NOTES));
        setLoaded(true);
      })
      .catch(() => setLoaded(true))
      .finally(() => setLoading(false));
  };

  const content = (
    <div style={{ width: 340, maxHeight: 360, overflowY: "auto" }}>
      {loading ? (
        <div style={{ textAlign: "center", padding: "16px 0" }}>
          <Spin size="small" />
        </div>
      ) : notes.length === 0 ? (
        <div className="crm-sub" style={{ padding: "4px 0" }}>No notes yet.</div>
      ) : (
        notes.map((n, i) => (
          <div
            key={n.id}
            className="crm-note"
            style={{
              padding: "8px 0",
              borderBottom: i < notes.length - 1 ? "1px solid #f0f0f0" : "none",
            }}
          >
            <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{n.body}</div>
            <div className="crm-sub" style={{ marginTop: 4 }}>
              {(n.author && n.author.name) || "—"} · {fmtDate(n.created_at)}
            </div>
          </div>
        ))
      )}
    </div>
  );

  return (
    <Popover
      trigger="hover"
      placement="rightTop"
      mouseEnterDelay={0.4}
      onOpenChange={handleOpenChange}
      title={<span>Recent notes{officeName ? ` — ${officeName}` : ""}</span>}
      content={content}
    >
      {children}
    </Popover>
  );
}
